"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api-client";
import {
  IMPERSONATED_CLIENT_STORAGE_KEY as STORAGE_KEY,
  readStoredImpersonatedClient,
} from "@/lib/impersonation-storage";

interface ImpersonatedClient {
  id: string;
  name: string;
}

interface ImpersonationContextType {
  impersonatedClient: ImpersonatedClient | null;
  setImpersonatedClient: (client: ImpersonatedClient | null) => void;
  clearImpersonation: () => void;
  isImpersonating: boolean;
  // True once the initial impersonation state is settled: either a stored
  // client was loaded, an admin's first clinic was auto-selected, or the
  // user isn't an admin so no impersonation is needed. Client-scoped fetches
  // (menu, dashboard, subscriptions) must wait for this before firing —
  // otherwise they race the auto-select and 403 with "not associated with a
  // client" because X-Client-Id isn't in sessionStorage yet.
  ready: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [impersonatedClient, setImpersonatedClientState] = useState<ImpersonatedClient | null>(null);
  const [ready, setReady] = useState(false);

  // Load from sessionStorage on mount; if empty and the user is an admin,
  // auto-select the first clinic so they land on a valid impersonation
  // instead of racing client-scoped fetches with an empty X-Client-Id.
  // sessionStorage is per-tab, so a brand-new tab always starts empty here —
  // this auto-select is what gives it a sane default without falling back
  // to a shared, cross-tab identity.
  useEffect(() => {
    if (authLoading || ready) return;

    const stored = readStoredImpersonatedClient();
    if (stored) {
      setImpersonatedClientState(stored);
      setReady(true);
      return;
    }

    if (!user || !isAdmin()) {
      setReady(true);
      return;
    }

    let cancelled = false;
    api
      .listClients({ limit: 1 })
      .then(({ items }) => {
        if (cancelled || items.length === 0) return;
        const client = { id: items[0].id, name: items[0].name };
        setImpersonatedClientState(client);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(client));
      })
      .catch((error) => {
        console.error("Failed to auto-select impersonated client:", error);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, user, isAdmin, ready]);

  const setImpersonatedClient = useCallback((client: ImpersonatedClient | null) => {
    setImpersonatedClientState(client);
    if (client) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(client));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const clearImpersonation = useCallback(() => {
    setImpersonatedClient(null);
  }, [setImpersonatedClient]);

  return (
    <ImpersonationContext.Provider
      value={{
        impersonatedClient,
        setImpersonatedClient,
        clearImpersonation,
        isImpersonating: impersonatedClient !== null,
        ready,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error("useImpersonation must be used within an ImpersonationProvider");
  }
  return context;
}

// Re-exported for existing call sites; the implementations now live in
// impersonation-storage.ts so api-client.ts can depend on them without a
// circular import (this context module also depends on api-client.ts).
export { getImpersonatedClientId, scopedStorageKey } from "@/lib/impersonation-storage";
